/**
 * Instagram Service
 * 
 * Integração com Instagram Graph API para importação de fotos.
 * Requer conta Business/Creator no Instagram.
 * 
 * Configuração necessária:
 * 1. Criar app no Meta for Developers
 * 2. Adicionar "Instagram API with Instagram Login"
 * 3. Configurar VITE_INSTAGRAM_APP_ID e VITE_INSTAGRAM_REDIRECT_URI
 */

// Configuração - Definir no .env
const INSTAGRAM_APP_ID = import.meta.env.VITE_INSTAGRAM_APP_ID || '';
const INSTAGRAM_REDIRECT_URI = import.meta.env.VITE_INSTAGRAM_REDIRECT_URI || `${window.location.origin}/instagram-callback`;

// Permissões necessárias para ler mídias
const INSTAGRAM_SCOPE = 'instagram_business_basic,instagram_business_content_publish';

export interface InstagramMedia {
  id: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  mediaUrl: string;
  thumbnailUrl?: string;
  caption?: string;
  timestamp: string;
  permalink: string;
  hashtags: string[];
}

export interface InstagramUser {
  id: string;
  username: string;
  name?: string;
  profilePictureUrl?: string;
  mediaCount?: number;
}

/**
 * Inicia fluxo OAuth do Instagram
 */
export function startInstagramAuth(): void {
  if (!INSTAGRAM_APP_ID) {
    console.error('INSTAGRAM_APP_ID não configurado');
    alert('Configuração do Instagram não encontrada. Contate o suporte.');
    return;
  }

  const authUrl = new URL('https://api.instagram.com/oauth/authorize');
  authUrl.searchParams.set('client_id', INSTAGRAM_APP_ID);
  authUrl.searchParams.set('redirect_uri', INSTAGRAM_REDIRECT_URI);
  authUrl.searchParams.set('scope', INSTAGRAM_SCOPE);
  authUrl.searchParams.set('response_type', 'code');
  
  // Salva estado para validação
  const state = crypto.randomUUID();
  sessionStorage.setItem('instagram_oauth_state', state);
  authUrl.searchParams.set('state', state);

  window.location.href = authUrl.toString();
}

/**
 * Processa callback do OAuth
 * Deve ser chamado na página de callback
 */
export async function handleInstagramCallback(): Promise<string | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) {
    console.error('Instagram OAuth error:', error);
    return null;
  }

  // Valida state
  const savedState = sessionStorage.getItem('instagram_oauth_state');
  if (state !== savedState) {
    console.error('Invalid OAuth state');
    return null;
  }

  if (!code) {
    console.error('No authorization code received');
    return null;
  }

  // Troca code por access_token
  // NOTA: Em produção, isso deve ser feito no backend para proteger o client_secret
  try {
    // Por segurança, essa troca deve ser feita via Cloud Function
    // Aqui retornamos o code para o backend processar
    return code;
  } catch (error) {
    console.error('Error exchanging code:', error);
    return null;
  }
}

/**
 * Busca mídias do usuário autenticado
 * NOTA: Requer access_token obtido via backend
 */
export async function fetchUserMedia(accessToken: string, limit = 50): Promise<InstagramMedia[]> {
  try {
    const response = await fetch(
      `https://graph.instagram.com/me/media?fields=id,media_type,media_url,thumbnail_url,caption,timestamp,permalink&limit=${limit}&access_token=${accessToken}`
    );

    if (!response.ok) {
      throw new Error(`Instagram API error: ${response.status}`);
    }

    const data = await response.json();
    
    return (data.data || []).map((item: any) => ({
      id: item.id,
      mediaType: item.media_type,
      mediaUrl: item.media_url,
      thumbnailUrl: item.thumbnail_url,
      caption: item.caption || '',
      timestamp: item.timestamp,
      permalink: item.permalink,
      hashtags: extractHashtags(item.caption || ''),
    }));
  } catch (error) {
    console.error('Error fetching Instagram media:', error);
    return [];
  }
}

/**
 * Busca informações do usuário autenticado
 */
export async function fetchUserProfile(accessToken: string): Promise<InstagramUser | null> {
  try {
    const response = await fetch(
      `https://graph.instagram.com/me?fields=id,username,name,profile_picture_url,media_count&access_token=${accessToken}`
    );

    if (!response.ok) {
      throw new Error(`Instagram API error: ${response.status}`);
    }

    const data = await response.json();
    
    return {
      id: data.id,
      username: data.username,
      name: data.name,
      profilePictureUrl: data.profile_picture_url,
      mediaCount: data.media_count,
    };
  } catch (error) {
    console.error('Error fetching Instagram profile:', error);
    return null;
  }
}

/**
 * Extrai hashtags de um texto
 */
export function extractHashtags(text: string): string[] {
  const regex = /#(\w+)/g;
  const matches = text.match(regex) || [];
  return matches.map(tag => tag.substring(1).toLowerCase());
}

/**
 * Filtra mídias por hashtags de pesca
 */
export function filterFishingMedia(media: InstagramMedia[]): InstagramMedia[] {
  const fishingTags = [
    'pesca', 'fishing', 'pescaria', 'pescaesportiva', 'pescador',
    'tucunare', 'tucunaré', 'dourado', 'peacockbass', 'bassfishing',
    'catchandrelease', 'fishinglife', 'pesqueiro', 'flyfish', 'flyfishing',
    'sportfishing', 'pescafluvial', 'pescaderio', 'pescaembarranco',
    'amazon', 'amazonia', 'araguaia', 'pantanal', 'rio',
  ];

  return media.filter(item => {
    const hasImageOrVideo = item.mediaType === 'IMAGE' || item.mediaType === 'VIDEO';
    const hasFishingTag = item.hashtags.some(tag => fishingTags.includes(tag));
    const captionMentionsFishing = fishingTags.some(t => 
      item.caption?.toLowerCase().includes(t)
    );
    
    return hasImageOrVideo && (hasFishingTag || captionMentionsFishing);
  });
}

/**
 * URL do tutorial para converter conta para Business/Creator
 */
export const INSTAGRAM_BUSINESS_TUTORIAL_URL = 
  'https://help.instagram.com/502981923235522';

export default {
  startInstagramAuth,
  handleInstagramCallback,
  fetchUserMedia,
  fetchUserProfile,
  extractHashtags,
  filterFishingMedia,
  INSTAGRAM_BUSINESS_TUTORIAL_URL,
};
